import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Modal, EmptyState, formatDate } from "@/components/pcp";
import { Plus, User, Key, TrashSimple, Shield, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { comprimirImagem } from "@/lib/imagem";

const emptyUser = { name: "", email: "", password: "", role: "operador", cargo: "", telefone: "" };

const ROLES = [
  { value: "admin", label: "Administrador", desc: "Acesso total, gerencia usuários, altera perfis e senhas" },
  { value: "gestor", label: "Gestor", desc: "Cria/edita contratos, OS, importa planilhas, gerencia insumos e cria usuários (não altera perfil depois)" },
  { value: "supervisor", label: "Supervisor", desc: "Agenda equipes, registra apontamentos e NCs" },
  { value: "operador", label: "Operador", desc: "Registra apontamentos de produção (mobile)" },
  { value: "fiscal", label: "Fiscal (cliente)", desc: "Somente consulta: medições, OS e relatório fotográfico apenas dos contratos vinculados pelo Administrador" },
];
const ROLES_GESTOR_PODE_CRIAR = ["gestor", "supervisor", "operador"];

const roleBadge = {
  admin: { bg: "#0A0A0A", color: "#FFF" },
  gestor: { bg: "#FFCC00", color: "#0A0A0A" },
  supervisor: { bg: "#0055FF", color: "#FFF" },
  operador: { bg: "#F4F4F5", color: "#27272A" },
  fiscal: { bg: "#DCFCE7", color: "#15803D" },
};

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const rolesCriaveis = isAdmin ? ROLES : ROLES.filter((r) => ROLES_GESTOR_PODE_CRIAR.includes(r.value));
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [pwdModal, setPwdModal] = useState(null); // user obj
  const [newPwd, setNewPwd] = useState("");
  const [perfil, setPerfil] = useState(null);
  const [fiscalModal, setFiscalModal] = useState(null); // user fiscal → vincular contratos
  const [contratos, setContratos] = useState([]);
  const [selContratos, setSelContratos] = useState([]);
  const abrirFiscal = async (u) => {
    setFiscalModal(u); setSelContratos(u.contratos_ids || []);
    if (!contratos.length) api.get("/contratos").then((r) => setContratos(r.data || [])).catch(() => null);
  };
  const salvarFiscal = async () => {
    try { await api.put(`/users/${fiscalModal.id}/contratos`, { contratos_ids: selContratos }); toast.success("Contratos do usuário atualizados"); setFiscalModal(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao vincular"); }
  };
  const mudarRole = async (u, role) => {
    if (role === u.role) return;
    const lbl = ROLES.find((r) => r.value === role)?.label || role;
    if (!window.confirm(`Alterar o acesso de ${u.name} de "${u.role}" para "${lbl}"? A mudança vale imediatamente.`)) return;
    try { await api.put(`/users/${u.id}/role`, { role }); toast.success(`${u.name} agora é ${lbl}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao alterar acesso"); }
  };
  const salvarPerfil = async () => {
    if (!perfil.name.trim()) { toast.error("Informe o nome"); return; }
    try { await api.put(`/users/${perfil.id}/perfil`, { name: perfil.name, cargo: perfil.cargo || "", telefone: perfil.telefone || "", assinatura: perfil.assinatura || null }); toast.success("Perfil atualizado"); setPerfil(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Preencha nome, e-mail e senha");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Senha precisa ter ao menos 6 caracteres");
      return;
    }
    try {
      await api.post("/auth/register", form);
      toast.success(`Usuário criado! ${form.name} (${form.role}) já pode fazer login com ${form.email}`);
      setModal(false);
      setForm(emptyUser);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erro ao criar usuário";
      toast.error(typeof msg === "string" ? msg : "Erro ao criar usuário");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir usuário ${u.name} (${u.email})?\n\nEle perderá acesso imediatamente.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário excluído");
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erro";
      toast.error(typeof msg === "string" ? msg : "Erro ao excluir");
    }
  };

  const changePwd = async () => {
    if (!newPwd || newPwd.length < 6) { toast.error("Senha mínima 6 caracteres"); return; }
    try {
      await api.post(`/users/${pwdModal.id}/senha`, { new_password: newPwd });
      toast.success(`Senha alterada para ${pwdModal.name}`);
      setPwdModal(null);
      setNewPwd("");
    } catch { toast.error("Erro ao alterar senha"); }
  };

  return (
    <>
      <PageHeader
        testid="page-usuarios"
        title="Usuários do Sistema"
        subtitle={`${users.length} usuários cadastrados`}
        actions={
          <button className="btn btn-primary" onClick={() => { setForm(emptyUser); setModal(true); }} data-testid="btn-new-user">
            <Plus size={16} weight="bold" /> Novo usuário
          </button>
        }
      />
      <div className="page-body">
        {users.length === 0 ? <EmptyState title="Carregando…" /> : (
          <div className="pcp-card" style={{ overflowX: "auto" }}>
            <table className="pcp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Criado em</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isCurrent = u.id === currentUser?.id;
                  const roleStyle = roleBadge[u.role] || roleBadge.operador;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, background: "#F4F4F5", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <User size={16} weight="bold" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{u.name} {isCurrent && <span className="chip" style={{ marginLeft: 6, background: "#FFCC00" }}>Você</span>}</div>
                            {(u.cargo || u.telefone) && <div style={{ fontSize: "0.72rem", color: "#71717A" }} data-testid={`user-cargo-${u.id}`}>{[u.cargo, u.telefone].filter(Boolean).join(" · ")}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="font-mono text-sm">{u.email}</td>
                      <td>
                        {!isCurrent && isAdmin && u.role !== "diretor_operacional" ? (
                          <select className="select" value={u.role} onChange={(e) => mudarRole(u, e.target.value)} data-testid={`user-role-select-${u.id}`}
                            style={{ background: roleStyle.bg, color: roleStyle.color, fontWeight: 700, fontSize: "0.72rem", padding: "3px 24px 3px 8px", height: "auto", border: "none", borderRadius: 3, cursor: "pointer" }} title="Alterar nível de acesso">
                            {ROLES.map((r) => <option key={r.value} value={r.value} style={{ background: "#fff", color: "#0A0A0A" }}>{r.label}</option>)}
                          </select>
                        ) : (
                          <span className="badge" style={{ background: roleStyle.bg, color: roleStyle.color, textTransform: "capitalize" }} title={!isAdmin ? "Somente o Administrador altera o perfil" : undefined} data-testid={`user-role-badge-${u.id}`}>
                            {u.role}
                          </span>
                        )}
                      </td>
                      <td className="font-mono text-xs">{formatDate(u.created_at)}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setPerfil({ id: u.id, name: u.name, cargo: u.cargo || "", telefone: u.telefone || "", assinatura: u.assinatura || null })} title="Nome, cargo e telefone (responsável comercial)" data-testid={`btn-perfil-${u.id}`}>
                          <Pencil size={12} weight="bold" /> Perfil
                        </button>
                        {isAdmin && (
                          <button className="btn btn-outline btn-sm" onClick={() => { setPwdModal(u); setNewPwd(""); }} title="Alterar senha" data-testid={`btn-senha-${u.id}`}>
                            <Key size={12} weight="bold" /> Senha
                          </button>
                        )}
                        {isAdmin && (u.role === "fiscal" || u.role === "gestor") && (
                          <button className="btn btn-outline btn-sm" onClick={() => abrirFiscal(u)} title={u.role === "fiscal" ? "Contratos que o fiscal pode consultar" : "Restringir o gestor aos contratos marcados (vazio = acesso geral)"} data-testid={`btn-contratos-fiscal-${u.id}`}>
                            <Shield size={12} weight="bold" /> Contratos ({(u.contratos_ids || []).length})
                          </button>
                        )}
                        {!isCurrent && isAdmin && u.email !== "admin@pcpviaria.com" && (
                          <button className="btn btn-ghost btn-sm" onClick={() => remove(u)} title="Excluir" data-testid={`btn-excluir-${u.id}`}>
                            <TrashSimple size={14} weight="bold" color="#DC2626" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="pcp-card" style={{ padding: 20, marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Shield size={18} weight="bold" />
            <div className="overline" style={{ margin: 0 }}>O que cada perfil pode fazer</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {ROLES.map((r) => (
              <div key={r.value} style={{ padding: 12, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3 }}>
                <span className="badge" style={{ background: roleBadge[r.value].bg, color: roleBadge[r.value].color, textTransform: "capitalize" }}>{r.label}</span>
                <div style={{ fontSize: "0.8rem", color: "#52525B", marginTop: 8 }}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title="Novo usuário">
        <div style={{ display: "grid", gap: 12 }}>
          <div><div className="label">Nome completo *</div><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" placeholder="Ex: João Silva" /></div>
          <div><div className="label">E-mail *</div><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} data-testid="user-email" placeholder="joao@ostecnovias.com.br" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><div className="label">Cargo (impresso em propostas)</div><input className="input" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} data-testid="user-cargo" placeholder="Ex: Gerente Comercial" /></div>
            <div><div className="label">Telefone</div><input className="input" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="user-telefone" placeholder="(65) 9 9999-9999" /></div>
          </div>
          <div><div className="label">Senha inicial * (mínimo 6 caracteres)</div><input type="text" className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" placeholder="Ex: obra2026" /></div>
          <div>
            <div className="label">Perfil</div>
            <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
              {rolesCriaveis.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div style={{ fontSize: "0.72rem", color: "#71717A", marginTop: 6 }}>
              {ROLES.find((r) => r.value === form.role)?.desc}
              {!isAdmin && <div style={{ marginTop: 4 }} data-testid="user-role-aviso-gestor">Depois de criado, só o Administrador pode mudar o perfil.</div>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="btn-save-user">Criar usuário</button>
        </div>
      </Modal>

      <Modal open={!!perfil} onClose={() => setPerfil(null)} title="Perfil do usuário">
        {perfil && (
          <div style={{ display: "grid", gap: 10 }} data-testid="perfil-form">
            <div><div className="label">Nome completo *</div><input className="input" value={perfil.name} onChange={(e) => setPerfil({ ...perfil, name: e.target.value })} data-testid="perfil-nome" /></div>
            <div><div className="label">Cargo (impresso em propostas)</div><input className="input" value={perfil.cargo} onChange={(e) => setPerfil({ ...perfil, cargo: e.target.value })} data-testid="perfil-cargo" /></div>
            <div><div className="label">Telefone</div><input className="input" value={perfil.telefone} onChange={(e) => setPerfil({ ...perfil, telefone: e.target.value })} data-testid="perfil-telefone" /></div>
            <div><div className="label">Imagem da assinatura (opcional — PNG com fundo transparente; usada em propostas quando autorizado)</div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }} data-testid="perfil-assinatura-area">
                <div style={{ width: 160, height: 56, border: "1px dashed #D4D4D8", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", overflow: "hidden" }}>
                  {perfil.assinatura ? <img src={perfil.assinatura} alt="assinatura" style={{ maxWidth: 156, maxHeight: 52, objectFit: "contain" }} data-testid="perfil-assinatura-preview" /> : <span style={{ fontSize: "0.7rem", color: "#A1A1AA" }}>sem assinatura</span>}
                </div>
                <label className="btn btn-outline btn-sm" style={{ cursor: "pointer" }}>Enviar imagem
                  <input type="file" accept="image/png,image/jpeg" style={{ display: "none" }} data-testid="perfil-assinatura-input" onChange={async (e) => { const f = e.target.files[0]; e.target.value = ""; if (!f) return; try { setPerfil({ ...perfil, assinatura: await comprimirImagem(f, 600, 280000) }); } catch (err) { toast.error(err.message); } }} />
                </label>
                {perfil.assinatura && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPerfil({ ...perfil, assinatura: null })} data-testid="perfil-assinatura-remover">Remover</button>}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><button className="btn btn-outline" onClick={() => setPerfil(null)}>Cancelar</button><button className="btn btn-primary" onClick={salvarPerfil} data-testid="btn-salvar-perfil">Salvar</button></div>
          </div>
        )}
      </Modal>

      <Modal open={!!fiscalModal} onClose={() => setFiscalModal(null)} title={`${fiscalModal?.role === "gestor" ? "Contratos do gestor" : "Contratos sob fiscalização"} — ${fiscalModal?.name || ""}`}>
        <div style={{ display: "grid", gap: 8 }} data-testid="fiscal-contratos-modal">
          <div className="text-xs" style={{ color: "#71717A" }} data-testid="fiscal-contratos-ajuda">
            {fiscalModal?.role === "gestor"
              ? "Com contratos marcados, o gestor só enxerga e opera esses contratos (OS, apontamentos, medições, previsões, cronograma) e deixa de ver dashboard, comercial, relatórios gerais e usuários. Sem nenhum marcado, ele continua com acesso geral."
              : "O fiscal só enxerga medições, OS e relatórios fotográficos dos contratos marcados (somente consulta)."}
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #E4E4E7", borderRadius: 4 }}>
            {contratos.filter((c) => c.status !== "Orçamento").map((c) => (
              <label key={c.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 10px", borderBottom: "1px solid #F4F4F5", cursor: "pointer" }} data-testid={`fiscal-contrato-${c.id}`}>
                <input type="checkbox" checked={selContratos.includes(c.id)} onChange={(e) => setSelContratos((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))} data-testid={`fiscal-contrato-check-${c.id}`} />
                <span><strong>{c.numero}</strong> · {c.contratante}<div className="text-xs" style={{ color: "#A1A1AA" }}>{c.objeto?.slice(0, 90)}</div></span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setFiscalModal(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={salvarFiscal} data-testid="fiscal-contratos-salvar">Salvar ({selContratos.length})</button>
          </div>
        </div>
      </Modal>
      <Modal open={!!pwdModal} onClose={() => setPwdModal(null)} title={`Alterar senha — ${pwdModal?.name || ""}`}>
        <div style={{ display: "grid", gap: 12 }}>
          <div><div className="label">Nova senha (mínimo 6 caracteres)</div><input type="text" className="input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="new-pwd" /></div>
          <div style={{ fontSize: "0.8rem", color: "#52525B" }}>
            O usuário precisará entrar com essa nova senha na próxima vez que fizer login.
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setPwdModal(null)}>Cancelar</button>
          <button className="btn btn-primary" onClick={changePwd} data-testid="btn-save-pwd">Alterar senha</button>
        </div>
      </Modal>
    </>
  );
}
